import { Icon as LegacyIcon } from '@ant-design/compatible';
import * as Sentry from '@sentry/browser';
import { Input as AInput, Form } from 'antd';
import Select from 'components/Select/Select';
import TrackingButton from 'components/TrackingComponents/Button';
import TrackingInput from 'components/TrackingComponents/TrackingInput';
import messages from 'containers/ContactUsPage/messages';
import { ContactUsForm as IContactUsForm } from 'containers/ContactUsPage/types';
import { debounce } from 'lodash';
import shuffle from 'lodash/shuffle';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import translations from 'translations';
import { CustomerAcquisitionSource } from 'types/schema';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { checkEmailRegex } from 'utils/utilities';
import PageHeader from '../PageHeader/PageHeader';
import ArrowRight from './arrow-right.svg';
import topBg from './top-bg.png';
// import ExternalLink from './external-link.svg';
import { clearReferralParams, hasReferralParam } from '../../utils/referralUtils';
import Address from '../Address';
import SVGIcon from '../SVGIcon/SVGIcon';
import businessData from './businessData.ts';
import internalMessages from './messages';

interface IOwnProps {
  isSuccess: any;
  submitting: boolean;
  isCheckingEmail: boolean;
  validationEmailMessage: string;
  onSubmit: (values: IContactUsForm) => void;
  onCheckEmail: (data: string) => void;
}

type Props = IOwnProps & RouteComponentProps;

const Container = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: #ffffff;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 8pt;
  flex: 1;
  max-width: 720px;
  background-image: url(${topBg});
  background-repeat: no-repeat;
  background-size: cover;
  margin-bottom: 8pt;
  ${media.md`
    padding: 30pt 90pt;
  `};
  p {
    line-height: normal;
    margin: 8px 0;
  }
`;

const AntdInput = styled(AInput)<any>`
  border-radius: 8px !important;
`;

const PrefixIcon = styled(LegacyIcon)`
  color: rgba(0, 0, 0, 0.25);
`;

const ReadMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

const BusinessNoticeParahraph = styled.div`
  color: #9e9e9e;
  margin-bottom: 12px;
`;

const LandingPageAnchor = styled.a`
  color: #42a5f5;
`;

const ReadMoreAnchor = styled.a`
  color: #2e904e;
  font-weight: bold;
  border: 1px solid #2e904e;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    color: white;
    background-color: #2e904e;
  }
`;

const TitleSection = styled.h4`
  font-weight: 500;
  font-size: 20px;
  color: #808289;
  line-height: 22px;
  margin: 24px 0 12px;
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
`;

const SubmitButton = styled(TrackingButton)`
  margin: 8px auto 0px;
  font-size: 16px;
  color: #ffffff;
  width: fit-content !important;
  background-color: #4b9c63;
  display: flex;
  align-items: center;
  gap: 14px;
  &:hover {
    opacity: 0.7;
  }
`;
const acquisitionSource = [
  {
    label: 'Google',
    value: CustomerAcquisitionSource.Google,
  },
  {
    label: 'Facebook',
    value: CustomerAcquisitionSource.Facebook,
  },
  {
    label: 'Youtube',
    value: CustomerAcquisitionSource.Youtube,
  },
  {
    label: 'TikTok',
    value: CustomerAcquisitionSource.Tiktok,
  },
  {
    label: 'Instagram',
    value: CustomerAcquisitionSource.Instagram,
  },
  {
    label: translations(utilsMessages.otherWebsite),
    value: CustomerAcquisitionSource.OtherWebsite,
  },
  {
    label: translations(utilsMessages.kamereoSalesman),
    value: CustomerAcquisitionSource.KamereoSalesman,
  },
  {
    label: translations(utilsMessages.fromAcquaintance),
    value: CustomerAcquisitionSource.FriendsColleaguesAcquaintance,
  },
  {
    label: translations(utilsMessages.OOHAds),
    value: CustomerAcquisitionSource.OohAds,
  },
  {
    label: translations(utilsMessages.offlineEvent),
    value: CustomerAcquisitionSource.OfflineEvents,
  },
  {
    label: translations(utilsMessages.other),
    value: CustomerAcquisitionSource.Other,
  },
];
const ContactUsForm: FC<Props> = (props) => {
  const [form] = Form.useForm();
  const { isCheckingEmail, validationEmailMessage, isSuccess, submitting, onSubmit, onCheckEmail, history } = props;
  const query = history.location && (new URLSearchParams(history?.location?.search) as any);

  const emailValidationCallback = useRef<any>(null);
  const otherAcquisitionSourceInputRef = useRef<any>(null);
  const currentLang = window.localStorage.getItem('lang') || 'vi';
  const [isShowAcquisitionText, setIsShowAcquisitionText] = useState(false);

  const acquisitionSourceData = useMemo(() => {
    const dataShuffle = acquisitionSource.slice(0, acquisitionSource.length - 1);
    const acquisitionSourceList = shuffle(dataShuffle);
    acquisitionSourceList.push(acquisitionSource[acquisitionSource.length - 1]);
    return acquisitionSourceList;
  }, []);

  const isHasReferralParam = hasReferralParam('REF');

  const isHideAcquisitionSourceSelect = useMemo(() => {
    const contactIdFromLandingPage = window.localStorage.getItem('contactIdFromLandingPage') || '';
    return (
      contactIdFromLandingPage ||
      (query.get('acquisitionSource') &&
        (query.get('acquisitionSource') === CustomerAcquisitionSource.LandingPage ||
          query.get('acquisitionSource') === CustomerAcquisitionSource.Email ||
          query.get('acquisitionSource') === CustomerAcquisitionSource.Survey ||
          query.get('acquisitionSource') === CustomerAcquisitionSource.Hotline))
    );
  }, [query]);

  const checkExistEmail = useMemo(
    () =>
      debounce((email) => {
        onCheckEmail(email);
      }, 200),
    [],
  );

  const checkEmail = () => () => {
    const email = form.getFieldValue('email');
    return Promise.resolve().then(() => {
      if (!checkEmailRegex.test(String(email).toLowerCase())) {
        return;
      }
      return new Promise((resolve, reject) => {
        emailValidationCallback.current = {
          resolve: resolve,
          reject: reject,
        };
        checkExistEmail(email);
      });
    });
  };
  const onAcquisitionSourceChange = (value) => {
    if (value === CustomerAcquisitionSource.Other) {
      setIsShowAcquisitionText(true);
      return;
    }
    setIsShowAcquisitionText(false);
  };
  useEffect(() => {
    if (!isCheckingEmail && emailValidationCallback.current) {
      const { resolve, reject } = emailValidationCallback.current;
      if (validationEmailMessage) {
        reject(validationEmailMessage);
      }
      resolve();
      emailValidationCallback.current = null;
    }
    if (isSuccess) {
      form.resetFields();
      props.history.push('/contact-success');
    }
  }, [isCheckingEmail, isSuccess]);

  useEffect(() => {
    if (isShowAcquisitionText && otherAcquisitionSourceInputRef.current) {
      otherAcquisitionSourceInputRef.current.input.focus();
    }
  }, [isShowAcquisitionText]);

  useEffect(() => {
    form.setFieldsValue({
      companyName: query.get('businessName') || '',
      phoneNumber: query.get('phone') || '',
      email: query.get('email') || '',
      userName: query.get('userName') || '',
      roleAtBusiness: query.get('roleAtBusiness') || undefined,
      addressNo: query.get('addressNo') || '',
      acquisitionSource: query.get('acquisitionSource') || undefined,
      buyerType: query.get('buyerType') || undefined,
      customerRefCode: query.get('referralCode') || sessionStorage.getItem('referralCode') || undefined,
    });
    if (isHideAcquisitionSourceSelect) {
      form.setFieldValue('acquisitionSource', 'LANDING_PAGE');
    }
    if (isHasReferralParam) {
      form.setFieldValue('acquisitionSource', 'KAMEREO_SALESMAN');
    }
  }, [isHideAcquisitionSourceSelect, isHasReferralParam]);

  if (isSuccess) {
    return (
      <Container>
        <PageHeader size="large">
          <FormattedMessage {...messages.header} />
        </PageHeader>
        <FormattedMessage {...messages.sendSuccessfully} />
        <ReadMore>
          <ReadMoreAnchor
            target="_Blank"
            href={`https://kamereo.vn/${currentLang === 'vi' ? 've-kamereo' : 'about-kamereo'}`}
          >
            <FormattedMessage {...utilsMessages.readMoreAboutUs} />
          </ReadMoreAnchor>
        </ReadMore>
      </Container>
    );
  }
  return (
    <Container>
      <PageHeader size="large">
        <FormattedMessage {...messages.header} />
      </PageHeader>
      <BusinessNoticeParahraph>
        <FormattedMessage {...messages.businessNotice} />{' '}
        <LandingPageAnchor
          target="_Blank"
          rel="noopener noreferrer"
          href={`https://kamereo.vn/${currentLang === 'vi' ? 've-kamereo' : 'about-kamereo'}`}
        >
          <FormattedMessage {...messages.findOutAboutTheService} />
        </LandingPageAnchor>
      </BusinessNoticeParahraph>
      <Form
        form={form}
        onFinish={(values) => onSubmit(values)}
        onFinishFailed={(error) => Sentry.captureException(error, { level: Sentry.Severity.Warning })}
        className="login-form"
        size="large"
        layout="vertical"
      >
        <TitleSection>
          <FormattedMessage {...messages.businessInfo} />
        </TitleSection>
        <FormattedMessage {...utilsMessages.businessName}>
          {(businessName: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: businessName }}>
              {(fieldRequired: string) => (
                <FormattedMessage {...utilsMessages.businessNamePlaceHolder}>
                  {(businessPlaceHolder: string) => (
                    <FormItem
                      name="companyName"
                      colon={false}
                      required={true}
                      label={businessName}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                    >
                      <TrackingInput
                        prefix={<PrefixIcon type="shop" />}
                        placeholder={businessPlaceHolder}
                        options={{
                          action: 'Enter Company Name',
                          category: 'Contact Us Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.buyerType}>
          {(buyerType: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: buyerType }}>
              {(fieldRequired: string) => (
                <FormattedMessage {...utilsMessages.buyerTypePlaceHolder}>
                  {(buyerTypePlaceHolder) => (
                    <FormItem
                      name="buyerType"
                      colon={false}
                      required={true}
                      label={buyerType}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                    >
                      <Select
                        items={businessData.buyerType.map((item) => ({
                          label: item.name,
                          value: item.value,
                        }))}
                        placeholder={buyerTypePlaceHolder}
                      />
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        {/* <Row gutter={12}>
          <Col span={24}>
            <FormattedMessage {...utilsMessages.businessType}>
              {(companyName: string) => (
                <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: companyName }}>
                  {(fieldRequired: string) => (
                    <FormattedMessage {...utilsMessages.businessTypePlaceHolder}>
                      {(businessTypePlaceHolder) => (
                        <FormItem
                          name="businessType"
                          colon={false}
                          required={true}
                          label={companyName}
                          hasFeedback
                          rules={[
                            {
                              required: true,
                              message: fieldRequired,
                            },
                          ]}
                        >
                          <Select
                            items={businessTypeData}
                            placeholder={businessTypePlaceHolder}
                            onChange={() => form.setFieldValue('businessCategory', undefined)}
                          />
                        </FormItem>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          </Col>
        </Row> */}
        {/* <TitleSection>
          <FormattedMessage {...messages.businessAddress} />
        </TitleSection> */}
        <Address
          form={form}
          fieldName={{ city: 'provinceId', district: 'districtId', ward: 'wardId', addressDetail: 'addressNo' }}
          fieldNameFullText={{ city: 'provinceName', district: 'districtName', ward: 'wardName' }}
          isNotClearAddressNo={true}
        />
        <TitleSection>
          <FormattedMessage {...messages.businessRepresentative} />
        </TitleSection>
        <FormattedMessage {...utilsMessages.representativeName}>
          {(representativeName: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: representativeName }}>
              {(fieldRequired: string) => (
                <FormItem
                  name="userName"
                  colon={false}
                  required={true}
                  label={representativeName}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: fieldRequired,
                    },
                  ]}
                >
                  <TrackingInput
                    prefix={<PrefixIcon type="user" />}
                    placeholder={representativeName}
                    options={{
                      action: 'Enter Username',
                      category: 'Contact Us Form | Input',
                    }}
                  >
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.phone}>
          {(phoneNumber: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: phoneNumber }}>
              {(fieldRequired) => (
                <FormattedMessage {...utilsMessages.validatePhone}>
                  {(fieldValidate) => (
                    <FormItem
                      name="phoneNumber"
                      colon={false}
                      required={true}
                      label={phoneNumber}
                      hasFeedback
                      rules={[
                        { required: true, message: fieldRequired },
                        {
                          validator: (_, value) => {
                            if (!value || (value.length >= 10 && value.length <= 11)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(fieldValidate as string));
                          },
                        },
                      ]}
                      getValueFromEvent={(event) => {
                        const value = event.target.value.replace(/[^0-9]/g, '');
                        return value;
                      }}
                    >
                      <TrackingInput
                        prefix={<PrefixIcon type="phone" />}
                        placeholder={phoneNumber}
                        autoComplete="phone"
                        options={{
                          action: 'Enter Phone',
                          category: 'Contact Us Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.fieldEmail}>
          {(fieldEmail) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: 'Email' }}>
              {(fieldRequired) => (
                <FormItem
                  name="email"
                  colon={false}
                  required={true}
                  label={`Email`}
                  hasFeedback
                  rules={[
                    {
                      type: 'email',
                      message: fieldEmail,
                    },
                    { required: true, message: fieldRequired },
                    {
                      validator: checkEmail(),
                    },
                  ]}
                  normalize={(value: string) => value && value.replace(/\s/g, '')}
                >
                  <TrackingInput
                    prefix={<PrefixIcon type="mail" />}
                    placeholder="Email"
                    autoComplete="username"
                    options={{
                      action: 'Enter Email',
                      category: 'Contact Us Form | Input',
                    }}
                  >
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.roleAtBusiness}>
          {(companyName: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: companyName }}>
              {(fieldRequired: string) => (
                <FormattedMessage {...utilsMessages.rolePlaceHolder}>
                  {(rolePlaceHolder) => (
                    <FormItem
                      name="roleAtBusiness"
                      colon={false}
                      required={true}
                      label={companyName}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                    >
                      <Select
                        items={businessData.roleAtBusiness.map((item) => ({
                          label: item.name,
                          value: item.value,
                        }))}
                        placeholder={rolePlaceHolder}
                      />
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <div style={{ display: isHideAcquisitionSourceSelect || isHasReferralParam ? 'none' : 'block' }}>
          <FormattedMessage {...utilsMessages.howDidYouHearUs}>
            {(howDidYouHearUs) => (
              <FormattedMessage {...utilsMessages.acquisitionSourceValidate}>
                {(fieldRequired) => (
                  <FormattedMessage {...utilsMessages.acquisitionSourcePlaceHolder}>
                    {(acquisitionSourcePlaceHolder) => (
                      <FormItem
                        name="acquisitionSource"
                        colon={false}
                        required={true}
                        label={howDidYouHearUs}
                        hasFeedback
                        rules={[{ required: true, message: fieldRequired }]}
                      >
                        <Select
                          items={acquisitionSourceData}
                          placeholder={acquisitionSourcePlaceHolder}
                          onChange={onAcquisitionSourceChange}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </div>
        {isShowAcquisitionText && (
          <FormattedMessage {...utilsMessages.otherAcquisitionSourceValidate}>
            {(fieldRequired) => (
              <FormItem
                name="acquisitionSourceText"
                colon={false}
                required={true}
                hasFeedback
                rules={[{ required: true, message: fieldRequired }]}
              >
                <AntdInput ref={otherAcquisitionSourceInputRef} placeholder={fieldRequired} />
              </FormItem>
            )}
          </FormattedMessage>
        )}
        <FormattedMessage {...internalMessages.customerRefCode}>
          {(customerRefCode: string) => (
            <FormattedMessage {...internalMessages.customerRefCodePlaceHolder}>
              {(customerRefCodePlaceHolder) => (
                <FormItem name="customerRefCode" colon={false} required={false} label={customerRefCode}>
                  <TrackingInput placeholder={customerRefCodePlaceHolder} options={{}}>
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormItem style={{ marginTop: '12px' }}>
          <SubmitButton
            block
            type="primary"
            htmlType="submit"
            trackingCategory="Contact Us Form | Button"
            trackingAction="Submit Contact Us"
            loading={submitting}
          >
            <FormattedMessage {...utilsMessages.send} />
            <SVGIcon src={ArrowRight} />
          </SubmitButton>
        </FormItem>
        <p>
          <FormattedMessage {...messages.agreementPrompt} />{' '}
          <a
            target="_blank"
            href={
              currentLang === 'vi'
                ? `https://kamereo.vn/blog/wp-content/uploads/2024/11/Dieu-khoan-Dich-vu-Dieu-kien-Giao-dich-Chung.pdf`
                : `https://kamereo.vn/blog/wp-content/uploads/2024/11/TERMS-OF-SERVICE-GENERAL-TRANSACTION-CONDITIONS.pdf`
            }
            rel="nofollow"
          >
            <FormattedMessage {...messages.terms} />
            {/* <SVGIcon src={ExternalLink} /> */}
          </a>
          {' & '}
          <a
            target="_blank"
            href={
              currentLang === 'vi'
                ? `https://kamereo.vn/blog/wp-content/uploads/2024/11/Chinh-Sach-Bao-Mat.pdf`
                : `https://kamereo.vn/blog/wp-content/uploads/2024/11/Privacy-Policy.pdf`
            }
            rel="nofollow"
          >
            <FormattedMessage {...messages.privacyCondition} />
            {/* <SVGIcon src={ExternalLink} /> */}
          </a>
        </p>
        <p>
          <FormattedMessage {...messages.alreadyHasAccount} />{' '}
          <Link
            to={'/signin'}
            onClick={() => {
              const isHasReferralParam = hasReferralParam('ODOO_CUSTOMER_ID');
              if (isHasReferralParam) {
                clearReferralParams();
              }
            }}
          >
            <FormattedMessage {...utilsMessages.signin} />
          </Link>
        </p>
      </Form>
    </Container>
  );
};

export default ContactUsForm;
